import z from 'zod';

type ZodFormDataOptions = {
    strict?: boolean;
};

const defaultZodFormDataOptions: Required<ZodFormDataOptions> = {
    strict: true,
};

type ZodFormDataShapeValue = z.ZodString | z.ZodFile;
type ZodFormDataShapeEntries =
    | ZodFormDataShapeValue
    | z.ZodOptional<z.ZodNullable<ZodFormDataShapeValue>>
    | z.ZodNullable<ZodFormDataShapeValue>
    | z.ZodOptional<ZodFormDataShapeValue>;

/**
 * Defines a Zod schema for parsing FormData instances.
 *
 * @param options.strict Whether extra keys should lead to errors (default=`true`)
 */
export function zodFormData<T extends Record<string, ZodFormDataShapeEntries>>(
    shape: T,
    options: Partial<ZodFormDataOptions> = {},
): z.ZodCustom<FormData, FormData> {
    const { strict } = Object.assign({}, defaultZodFormDataOptions, options);
    const entries = Object.entries(shape).map(([key, value]) => ({
        key,
        schema: value,
        supportsNullish: value.safeParse(null).success || value.safeParse(undefined).success,
    }));

    return z.instanceof(FormData).superRefine((val, ctx) => {
        if (strict) {
            const superfluousKeys = [...val.keys()].filter((key) => !(key in shape));
            if (superfluousKeys.length > 0) {
                ctx.addIssue({
                    code: 'unrecognized_keys',
                    keys: superfluousKeys,
                });
            }
        }

        for (const { key, schema, supportsNullish } of entries) {
            if (!val.has(key) && !supportsNullish) {
                ctx.addIssue({
                    code: 'custom',
                    message: `Expected FormData to have field "${key}"`,
                    path: [key],
                    input: val,
                });
                continue;
            }

            const result = schema.safeParse(val.get(key));
            if (!result.success) {
                for (const issue of result.error.issues) {
                    if (typeof issue === 'object' && issue !== null) {
                        ctx.addIssue({
                            ...issue,
                            path: [...issue.path, key],
                        });
                    }
                }
            }
        }
    });
}
